"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import {
  generateEdit,
  getEditStatus,
  EditStatusResponse,
} from "@/lib/api";

const POLL_INTERVAL = 5000; // 5 seconds

interface UseImageEditOptions {
  onComplete?: (edit: EditStatusResponse) => void;
  onError?: (error: string) => void;
}

export function useImageEdit(options?: UseImageEditOptions) {
  const [isEditing, setIsEditing] = useState(false);
  const [currentEditId, setCurrentEditId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<EditStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(
    async (editId: string) => {
      if (!isMountedRef.current) return;

      try {
        const status = await getEditStatus(editId);

        if (!isMountedRef.current) return;

        setEditStatus(status);

        if (status.status === "completed") {
          stopPolling();
          setIsEditing(false);
          options?.onComplete?.(status);
        } else if (status.status === "failed") {
          stopPolling();
          setIsEditing(false);
          setError(status.error_message || "Image edit failed");
          options?.onError?.(status.error_message || "Image edit failed");
        }
        // Keep polling for pending/processing status
      } catch (err) {
        if (!isMountedRef.current) return;
        console.error("Error polling edit status:", err);
        // Continue polling even on error
      }
    },
    [stopPolling, options]
  );

  const startPolling = useCallback(
    (editId: string) => {
      stopPolling();

      // Initial poll
      pollStatus(editId);

      // Set up interval
      pollIntervalRef.current = setInterval(() => {
        pollStatus(editId);
      }, POLL_INTERVAL);
    },
    [pollStatus, stopPolling]
  );

  const edit = useCallback(
    async (imageId: string, editPrompt: string) => {
      setIsEditing(true);
      setError(null);
      setEditStatus(null);

      try {
        const response = await generateEdit({
          image_id: imageId,
          edit_prompt: editPrompt,
        });

        setCurrentEditId(response.edit_id);
        setEditStatus({
          edit_id: response.edit_id,
          status: response.status as "pending" | "processing" | "completed" | "failed",
        });

        // Start polling for status updates
        startPolling(response.edit_id);

        return response.edit_id;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to start image edit";
        setError(errorMessage);
        setIsEditing(false);
        options?.onError?.(errorMessage);
        throw err;
      }
    },
    [startPolling, options]
  );

  const reset = useCallback(() => {
    stopPolling();
    setIsEditing(false);
    setCurrentEditId(null);
    setEditStatus(null);
    setError(null);
  }, [stopPolling]);

  return {
    isEditing,
    currentEditId,
    editStatus,
    error,
    edit,
    reset,
    stopPolling,
  };
}
